import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, X } from "lucide-react";
import { FLEETRIFYROUTEPATH } from "../routes/routesPath";

type PlanErrorCode = "PLAN_LIMIT_REACHED" | "NO_ACTIVE_SUBSCRIPTION";

interface PlanLimitError {
    message: string;
    code: PlanErrorCode;
    resource?: string;
    upgrade_to?: { id: string; name: string; price?: number } | null;
}

interface ToastAction {
    label: string;
    onClick: () => void;
    variant?: "primary" | "link";
}

const PLAN_CODES: PlanErrorCode[] = ["PLAN_LIMIT_REACHED", "NO_ACTIVE_SUBSCRIPTION"];

// Returns the API's plan error (403 + known code), or null for any other error.
export const getPlanLimitError = (err: any): PlanLimitError | null => {
    const error = err?.data?.error;
    if (err?.status !== 403 || !PLAN_CODES.includes(error?.code)) return null;
    return error;
};

// Bulk uploads return 201 even when some rows were skipped for the plan limit.
const isPartialUpload = (message: string) => /not invited|not added/i.test(message);

const showPlanToast = (message: string, actions: ToastAction[], duration = 15000) => {
    toast.custom(
        (id) => (
            <div
                role="alert"
                className="w-[356px] max-w-[calc(100vw-32px)] bg-[#FFF8E6] border border-[#F5C451] rounded-lg shadow-lg p-4 flex gap-3"
            >
                <AlertTriangle size={18} className="shrink-0 mt-0.5 text-[#B7791F]" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                    <p className="text-[13px] leading-relaxed text-[#5C3D00]">{message}</p>
                    {actions.length > 0 && (
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                            {actions.map((action) => (
                                <button
                                    key={action.label}
                                    type="button"
                                    onClick={() => {
                                        toast.dismiss(id);
                                        action.onClick();
                                    }}
                                    className={
                                        action.variant === "link"
                                            ? "text-xs font-semibold text-[#143887] underline hover:text-blue-800"
                                            : "text-xs font-semibold text-white bg-[#143887] hover:bg-[#0e2b63] px-3 py-1.5 rounded-md"
                                    }
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    onClick={() => toast.dismiss(id)}
                    className="shrink-0 self-start text-[#B7791F] hover:text-[#5C3D00]"
                    aria-label="Dismiss"
                >
                    <X size={14} />
                </button>
            </div>
        ),
        { duration }
    );
};

/**
 * Plan limits for inviting staff, adding branches, services and fleet. The API
 * rejects with 403 PLAN_LIMIT_REACHED / NO_ACTIVE_SUBSCRIPTION; show its message
 * as sent, with a button to upgrade or renew.
 */
export const usePlanLimit = () => {
    const navigate = useNavigate();

    const openPlans = useCallback(
        (planId?: string) => {
            const params = new URLSearchParams({ tab: "subscription", compare: "1" });
            if (planId) params.set("plan", planId);
            navigate(`${FLEETRIFYROUTEPATH.SETTINGS}?${params.toString()}`);
        },
        [navigate]
    );

    /** Shows the plan error if `err` is one. Returns true when it was handled. */
    const handlePlanLimitError = useCallback(
        (err: any): boolean => {
            const planError = getPlanLimitError(err);
            if (!planError) return false;

            const actions: ToastAction[] = [];
            if (planError.code === "PLAN_LIMIT_REACHED") {
                const target = planError.upgrade_to;
                actions.push(
                    target?.id
                        ? { label: `Upgrade to ${target.name}`, onClick: () => openPlans(target.id) }
                        : { label: "View plans", onClick: () => openPlans() }
                );
                if (planError.resource === "users") {
                    actions.push({
                        label: "Manage pending invites",
                        variant: "link",
                        onClick: () => navigate(`${FLEETRIFYROUTEPATH.USER}?view=invitation`),
                    });
                }
            } else {
                actions.push({ label: "Renew plan", onClick: () => openPlans() });
            }

            showPlanToast(planError.message, actions);
            return true;
        },
        [navigate, openPlans]
    );

    /** Bulk upload succeeded: warn (with Upgrade) if some rows were skipped. */
    const showBulkUploadResult = useCallback(
        (message: string | undefined, fallback: string) => {
            const text = message || fallback;
            if (isPartialUpload(text)) {
                showPlanToast(text, [{ label: "Upgrade plan", onClick: () => openPlans() }]);
            } else {
                toast.success(text);
            }
        },
        [openPlans]
    );

    return { handlePlanLimitError, showBulkUploadResult, openPlans };
};
